import { initializeApp } from "firebase/app";
import {
  getFirestore,
  doc,
  getDoc,
  connectFirestoreEmulator,
} from "firebase/firestore";
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from "firebase/functions";
import { getAnalytics, isSupported } from "firebase/analytics";
import { getAuth, connectAuthEmulator } from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyBHxrrZo7wEHT1o5qj4V2FCtMXqIue4WGA",
  authDomain: "guidehub-2cf45.firebaseapp.com",
  databaseURL: "https://guidehub-2cf45-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "guidehub-2cf45",
  storageBucket: "guidehub-2cf45.appspot.com",
  messagingSenderId: "901643399518",
  appId: "1:901643399518:web:2adf8cad6f856906019fcf",
  measurementId: "G-T5KN2RES26",
};

const app = initializeApp(firebaseConfig);

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

const isLocalHost = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return LOCAL_HOSTNAMES.has(window.location.hostname);
};

const shouldEnableAnalytics = () => !isLocalHost();

let analytics = null;
isSupported()
  .then((yes) => {
    if (yes && shouldEnableAnalytics()) {
      analytics = getAnalytics(app);
    }
  })
  .catch(() => {
    console.log("Analytics not supported in this environment");
  });

const FUNCTION_REGION = "europe-central2";
const functions = getFunctions(app, FUNCTION_REGION);
const db = getFirestore(app);
const auth = getAuth(app);

const getQueryParam = (key) => {
  if (typeof window === "undefined") {
    return null;
  }
  return new URLSearchParams(window.location.search).get(key);
};

const isTruthyFlag = (value) =>
  typeof value === "string" &&
  ["1", "true", "yes", "on"].includes(value.toLowerCase());

const isFalsyFlag = (value) =>
  typeof value === "string" &&
  ["0", "false", "no", "off"].includes(value.toLowerCase());

const shouldUseEmulators = () => {
  const queryOverride = getQueryParam("useEmulators");
  if (isTruthyFlag(queryOverride)) {
    return true;
  }
  if (isFalsyFlag(queryOverride)) {
    return false;
  }

  const envOverride = process.env.REACT_APP_USE_FIREBASE_EMULATORS;
  if (isTruthyFlag(envOverride)) {
    return true;
  }
  if (isFalsyFlag(envOverride)) {
    return false;
  }

  return isLocalHost();
};

const getEmulatorConfig = () => ({
  host: process.env.REACT_APP_FIREBASE_EMULATOR_HOST || "localhost",
  functionsPort: Number(process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_PORT || 5002),
  firestorePort: Number(process.env.REACT_APP_FIREBASE_FIRESTORE_EMULATOR_PORT || 8082),
  authPort: Number(process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_PORT || 9092),
});

const initializeEmulators = () => {
  if (!shouldUseEmulators()) {
    return false;
  }

  const emulatorConfig = getEmulatorConfig();

  try {
    console.log("Using Firebase emulators", {
      host: emulatorConfig.host,
      functionsPort: emulatorConfig.functionsPort,
      firestorePort: emulatorConfig.firestorePort,
      authPort: emulatorConfig.authPort,
      origin: typeof window !== "undefined" ? window.location.origin : "unknown",
    });

    connectFunctionsEmulator(
      functions,
      emulatorConfig.host,
      emulatorConfig.functionsPort,
    );
    connectFirestoreEmulator(
      db,
      emulatorConfig.host,
      emulatorConfig.firestorePort,
    );
    connectAuthEmulator(
      auth,
      `http://${emulatorConfig.host}:${emulatorConfig.authPort}`,
      { disableWarnings: true },
    );

    return true;
  } catch (error) {
    console.error("Failed to initialize emulators:", error);
    return false;
  }
};

const useEmulators = initializeEmulators();

const createCloudFunction = (name) => {
  const functionRef = httpsCallable(functions, name);

  return async (data, retries = 2) => {
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        console.log(`Calling ${name} (attempt ${attempt + 1}/${retries + 1})`, {
          functionName: name,
          environment: process.env.NODE_ENV,
          emulators: useEmulators,
          region: FUNCTION_REGION,
        });

        const result = await functionRef(data);

        console.log(`${name} response`, {
          status: result?.data?.status,
          error: result?.data?.detailedErrorCode,
        });

        return result;
      } catch (error) {
        console.error(`Error in ${name} (attempt ${attempt + 1})`, {
          message: error.message,
          code: error.code,
          details: error.details,
          functionName: name,
          attempt: attempt + 1,
        });

        if (error.message?.includes("CORS")) {
          const emulatorConfig = getEmulatorConfig();
          console.error("CORS error detected", {
            origin: typeof window !== "undefined" ? window.location.origin : "unknown",
            functionUrl: `http://${emulatorConfig.host}:${emulatorConfig.functionsPort}/${firebaseConfig.projectId}/${FUNCTION_REGION}/${name}`,
            emulatorStatus: useEmulators,
          });
        }

        if (attempt === retries) {
          throw error;
        }

        const delay = Math.pow(2, attempt) * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  };
};

const createPaymentIntent = createCloudFunction("createPaymentIntent");
const confirmAuthorizationCompletion = createCloudFunction(
  "confirmAuthorizationCompletion",
);
const confirmPaymentIntent = createCloudFunction("confirmPaymentIntent");

export default db;
export {
  createPaymentIntent,
  confirmAuthorizationCompletion,
  confirmPaymentIntent,
  auth,
  functions,
  doc,
  getDoc,
  analytics,
  useEmulators,
};
